import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { filter, tap } from 'rxjs';

import { LoadingScreenComponent } from '@shared/ui/loading-screen/loading-screen.component';

@Injectable({ providedIn: 'root' })
export class LoadingScreenService {
  private overlayRef: OverlayRef | null = null;

  // DI
  private readonly overlay = inject(Overlay);
  private readonly router = inject(Router);

  constructor() {
    // A completed navigation clears a stranded overlay — the safety net for a caller that never
    // reaches its finalize().
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        tap(() => this.setIsLoading(false)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  // The overlay is the state: both branches below are idempotent, so no separate flag is kept.
  setIsLoading(isLoading: boolean): void {
    if (isLoading) {
      this.showOverlay();
    } else {
      this.disposeOverlay();
    }
  }

  private showOverlay(): void {
    if (this.overlayRef) return;

    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
      panelClass: 'loading-screen-overlay',
      positionStrategy: this.overlay.position().global(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });

    const portal = new ComponentPortal(LoadingScreenComponent);
    this.overlayRef.attach(portal);
  }

  private disposeOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}
